import React, { useState, useRef, useEffect } from "react";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import { INQUIRY_TAB_LIST_URL, INQUIRY_REGISTER_URL, INQUIRY_EDIT_URL } from "../../utils/urls";
import instance from "../../services/services";
import PopupLoginError from "../../components/Popup/LoginError";
import { useLanguage } from "../../hooks/useLanguage";

const isImageFile = (fileOrUrl) => {
  if (!fileOrUrl) return false;
  if (fileOrUrl.type) return fileOrUrl.type.startsWith('image');
  const url = typeof fileOrUrl === 'string' ? fileOrUrl : '';
  const cleanUrl = url.split('?')[0];
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(cleanUrl);
};

const convertBrToNewline = (value = '') => {
  return value
      .replace(/\r\n/g, '\n')
      .replace(/\n?<br\s*\/?>\n?/gi, '\n');
};

function CommInquiryForm(){
  const { t } = useLanguage();
  const { myInfo } = useOutletContext();
  const selectRef = useRef();
  const fileInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false); // 팝업
  
  // location.state에서 값 가져오기
  const inquiryType = location.state?.inquiryType;
  const isEdit = location.state?.isEdit || false;
  const editData = location.state?.editData || null;
  const editFiles = location.state?.editFiles || [];
  
  // 상태 초기화 - 수정 모드일 경우 기존 데이터로 초기화
  const [selectList, setSelectList] = useState([]);
  const [state, setState] = useState({
    isReason: false,
    subject: isEdit ? editData?.subject || '' : '',
    content: isEdit
        ? convertBrToNewline(editData?.contents || '')
        : '',
    selectValue: isEdit ? editData?.board_type_cd || '' : '',
    selectedName: isEdit ? editData?.board_type || '' : ''
  });
  
  // 이미지 상태 - 수정 모드일 경우 기존 이미지로 초기화
  const [previewImages, setPreviewImages] = useState(
    isEdit 
      ? editFiles.map(file => ({
          isExisting: true,
          file_seq: file.file_seq,
          url: file.new_filepath
        }))
      : []
  );
  
  // 문의유형 셀렉박스
  const handleSelectOpen = () => {
    setState(prev => ({ 
      ...prev, 
      isReason: !prev.isReason,
    }));
  };

  // 문의유형 셀렉박스 선택 핸들러
  const handleSelectedItem = (select) => {
    setState(prev => ({
      ...prev,
      isReason: false,
      selectValue: select.codecd,
      selectedName: select.value
    }));
  };

  // 입력 필드 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 이미지 파일 선택 핸들러 - 더 간단한 버전
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // 최대 5개까지만 허용
    const remainingSlots = 5 - previewImages.length;
    const selectedFiles = files.slice(0, remainingSlots);
    
    if (selectedFiles.length === 0) return;
    
    // 미리보기 이미지 URL 생성
    const newPreviewImages = selectedFiles.map(file => ({
      isExisting: false,
      file,
      url: URL.createObjectURL(file)
    }));
    
    setPreviewImages(prev => [...prev, ...newPreviewImages]);
    
    // 파일 선택 후 input 값 초기화
    e.target.value = null;
  };

  // 이미지 삭제 핸들러 - 간단한 버전
  const handleImageDelete = (index) => {
    const imageToDelete = previewImages[index];
    
    // 새로 추가한 이미지인 경우 URL 해제
    if (!imageToDelete.isExisting) {
      URL.revokeObjectURL(imageToDelete.url);
    }
    
    // previewImages에서 제거
    setPreviewImages(prev => prev.filter((_, i) => i !== index));
  };

  // 문의 유형 리스트
  useEffect(() => {
    const fetchInquiryTabList = async () => {
      try {
        const response = await instance.post(INQUIRY_TAB_LIST_URL);
        setSelectList(response.data.data);
        
        // 수정 모드이지만 board_type_cd가 없는 경우, 서버에서 가져온 목록에서 찾기
        if (isEdit && editData && editData.board_type && !state.selectValue) {
          const matchingType = response.data.data.find(item => item.value === editData.board_type);
          if (matchingType) {
            setState(prev => ({
              ...prev,
              selectValue: matchingType.codecd,
              selectedName: matchingType.value
            }));
          }
        }
      } catch (error) {
        console.error("문의유형 리스트 가져오기 실패", error);
      }
    };
    fetchInquiryTabList();
  }, [isEdit, editData, state.selectValue]);

  const handleCancel = () => {
    if (inquiryType === 'mypage') {
      navigate('/myPage/inquiry');
    } else {
      navigate('/community/inquiry');
    }
  };

  // 문의 등록 또는 수정
  const handleRegister = async () => {
    // 입력값 검증
    if (!state.selectValue) {
      setIsPopupOpen(t('inquiry_type_text'));
      return;
    }
    
    if (!state.subject) {
      setIsPopupOpen(t('title_text'));
      return;
    }
    
    if (!state.content) {
      setIsPopupOpen(t('content_text'));
      return;
    }

    try {
      const formData = new FormData();
      
      // 기본 정보 추가
      formData.append('boardid', 'mbqna');
      formData.append('board_type', state.selectValue);
      formData.append('subject', state.subject);
      formData.append('contents', state.content);
      
      // 수정 모드인 경우
      if (isEdit && editData) {
        formData.append('b_seq', editData.b_seq);
        
        // 삭제된 이미지 파일 시퀀스 추가
        const existingFilesInEdit = editFiles.map(file => file.file_seq);
        const keepingFileSeqs = previewImages
          .filter(img => img.isExisting)
          .map(img => img.file_seq);
          
        const deletedFileSeqs = existingFilesInEdit.filter(seq => !keepingFileSeqs.includes(seq));
        
        if (deletedFileSeqs.length > 0) {
          deletedFileSeqs.forEach(seq => {
            formData.append('delete_file_id[]', seq);
          });
        }
      }
      
      // 새로운 이미지 파일 추가 - previewImages에서 직접 추출
      previewImages
        .filter(img => !img.isExisting)
        .forEach(img => {
          formData.append('upload_images[]', img.file);
        });
      
      // API URL 결정
      const apiUrl = isEdit ? INQUIRY_EDIT_URL : INQUIRY_REGISTER_URL;
      
      const response = await instance.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setIsPopupOpen({
          message: isEdit ? t('inquiry_modified') : t('inquiry_registered'),
          action: 'navigate_inquiry'
        });
      } else {
        setIsPopupOpen({
          message: isEdit ? t('inquiry_failed') : t('inquiry_failed2'),
          action: 'navigate_inquiry'
        });
      }
    } catch (error) {
      console.error(isEdit ? "문의 수정 실패" : "문의 등록 실패", error);
      setIsPopupOpen({
        message: isEdit ? '문의 수정 중 오류가 발생했습니다.' : '문의 등록 중 오류가 발생했습니다.',
        action: 'navigate_inquiry'
      });
    }
  };

  // 팝업 확인 버튼 클릭 핸들러 추가
  const handleConfirm = () => {
    if (isPopupOpen.action === 'navigate_login') {
      navigate('/myPage/inquiry'); // 페이지 이동
    } else if (isPopupOpen.action === 'navigate_inquiry') {
      handleCancel(); // 원래 취소 버튼 함수 사용해서 적절한 페이지로 이동
    }
    setIsPopupOpen(false); // 팝업 닫기
  };

  return(
    <div className="board_container order_inner">
      <h3 className="commu_title">{isEdit ? t('inquiry_modified2') : t('1_qustion')}</h3>
      <ul className="default_info_table board_list inquiry_form">
        <li className="info_item user_id">
          <div className="label">
            <label htmlFor="userid">{t('id')}</label>
          </div>
          <div className="form_info">
            <div className="info_detail default_input w-half">
              <input
                type="text"
                className="readonly"
                id="userid"
                value={myInfo?.userid}
                readOnly
              />
            </div>
          </div>
        </li>

        <li className="info_item inquiry_type">
          <div className="label">
            <span>{t('inquiry_type')}</span>
          </div>
          <div className="form_info">
            <div className="info_detail default_input w-half">
              <div 
                ref={selectRef}
                className={`default_select border ${state.isReason ? 'open' : ''}`}
              >
                <button
                  type="button"
                  className={`select_btn gray_icon ${state.selectValue ? 'active' : ''}`}
                  onClick={handleSelectOpen}
                >
                  <span className="ellipsis_clamp">
                    {state.selectedName || t('inquiry_type_selection')}
                  </span>
                </button>
                <ul className="select_list">
                {selectList.map((select) => (
                  <li
                    key={select.codecd}
                    className={`ellipsis1 select_item ${state.selectValue === select.codecd ? 'active' : ''}`}
                    onClick={() => handleSelectedItem(select)}
                  >
                    {select.value}
                  </li>
                ))}
                </ul>
              </div>
            </div>
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="subject">{t('title')}</label>
          </div>
          <div className="form_info">
            <div className="info_detail default_input">
              <input
                type="text"
                name="subject"
                id="subject"
                placeholder={t('title_input')}
                value={state.subject}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="content">{t('content')}</label>
          </div>
          <div className="form_info">
            <div className="info_detail default_input">
              <textarea
                name="content"
                id="content"
                placeholder={t('inquiry_content')}
                value={state.content}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <span>{t('file_attachment')}</span>
            <span className="yellow"> *</span>
          </div>
          <div className="form_info flex-wrap">
            {/* 파일 첨부 */}
            <div className="info_detail">
              <div className="attach_file">
                <div className="img_attach_box file_attach_box">
                  {previewImages.length < 5 && (
                    <label
                      htmlFor="sub_img_attach"
                      className="default_btn ic_lg white attach_btn"
                    />
                  )}
                  <input
                    type="file"
                    className="i_hide"
                    id="sub_img_attach"
                    accept="*/*"
                    multiple
                    ref={fileInputRef}
                    onChange={handleFileChange}
                  />
                  <ul className="preview_list">
                    {previewImages.map((image, index) => (
                      <li key={index} className="default_btn ic_lg">
                        {isImageFile(image.file || image.url) ? (
                          <img
                            className="object-cover"
                            src={image.url}
                            alt={`첨부 이미지 ${index + 1}`}
                            loading="lazy"
                          />
                        ) : (
                          <p className="file_text">
                            {image.file?.name || image.url?.split('/').pop()?.split('?')[0] || `파일 ${index + 1}`}
                          </p>
                        )}
                        <button
                          type="button"
                          className="delete_btn"
                          onClick={() => handleImageDelete(index)}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="dash_list flex-2 column w-100 commu_img">
                  <span>{t('file_capacity_limit')}</span>
                  <span>{t('maximum_5_images')}</span>
                </p>
              </div>
            </div>
          </div>
        </li>
      </ul>

      {/* 목록 버튼 */}
      <div className="submit_box bg mt_32">
        <button
          className="default_btn white lg"
          onClick={handleCancel}
        >{t('cancel')}
        </button>
        <button
          className="default_btn black lg"
          onClick={handleRegister}
        >{isEdit ? t('modify') : t('register')}
        </button>
      </div>

      <PopupLoginError isPopup={isPopupOpen} setIsPopup={setIsPopupOpen} onConfirm={handleConfirm}/>

    </div>
  );
}

export default CommInquiryForm;
